import type {
	CreateMainFunctionBody,
	CreateSubFunctionBody,
	ListMainFunctionsQuery,
	ListSubFunctionsQuery,
	MainFunctionListItem,
	MainFunctionOption,
	MainFunctionStats,
	Paginated,
	SubFunctionListItem,
	SubFunctionOption,
	UpdateMainFunctionBody,
	UpdateSubFunctionBody,
} from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "../api/client";
import { queryClient } from "../api/query-client";
import { scopeOptionsOptions } from "../users/queries";

/**
 * One key family for the whole accreditation-category tree. Main and sub
 * functions invalidate together on purpose: a sub-function write changes the
 * parent row's `sub_functions_count`, and a main-function rename shows on
 * every sub-function row.
 */
export const functionsKeys = {
	all: ["functions"] as const,
	mainList: (q: ListMainFunctionsQuery) => ["functions", "main", "list", q] as const,
	mainDetail: (id: string) => ["functions", "main", "detail", id] as const,
	mainOptions: ["functions", "main", "options"] as const,
	mainStats: ["functions", "main", "stats"] as const,
	subList: (q: ListSubFunctionsQuery) => ["functions", "sub", "list", q] as const,
	subOptions: ["functions", "sub", "options"] as const,
};

/** One request → page + total + every row's usage counts (the API aggregates in SQL). */
export const mainFunctionsListOptions = (q: ListMainFunctionsQuery) =>
	queryOptions({
		queryKey: functionsKeys.mainList(q),
		queryFn: ({ signal }) => api.get<Paginated<MainFunctionListItem>>("/main-functions", { query: q, signal }),
		// Keep the previous page on screen while the next loads — no blank flash.
		placeholderData: keepPreviousData,
		staleTime: 60_000,
	});

/** One main function with its counts — the drill-down page's header. */
export const mainFunctionDetailOptions = (id: string) =>
	queryOptions({
		queryKey: functionsKeys.mainDetail(id),
		queryFn: ({ signal }) => api.get<MainFunctionListItem>(`/main-functions/${id}`, { signal }),
		staleTime: 60_000,
	});

/** The whole list, for `<select>` elements — admin-curated, tens of rows. */
export const mainFunctionOptionsOptions = () =>
	queryOptions({
		queryKey: functionsKeys.mainOptions,
		queryFn: ({ signal }) => api.get<MainFunctionOption[]>("/main-functions/options", { signal }),
		staleTime: 5 * 60_000,
	});

export const subFunctionsListOptions = (q: ListSubFunctionsQuery) =>
	queryOptions({
		queryKey: functionsKeys.subList(q),
		queryFn: ({ signal }) => api.get<Paginated<SubFunctionListItem>>("/sub-functions", { query: q, signal }),
		placeholderData: keepPreviousData,
		staleTime: 60_000,
	});

/** Every sub-function, for the dependent dropdown — filtered client-side by `main_function_id`. */
export const subFunctionOptionsOptions = () =>
	queryOptions({
		queryKey: functionsKeys.subOptions,
		queryFn: ({ signal }) => api.get<SubFunctionOption[]>("/sub-functions/options", { signal }),
		staleTime: 5 * 60_000,
	});

/** Also the user scope editor's dropdowns (`/users/scope-options` lists main and sub functions) — else a new or renamed one is missing there for 5 minutes. */
export const invalidateFunctions = () =>
	Promise.all([queryClient.invalidateQueries({ queryKey: functionsKeys.all }), queryClient.invalidateQueries({ queryKey: scopeOptionsOptions().queryKey })]);

export const createMainFunction = (body: CreateMainFunctionBody) => api.post<MainFunctionListItem>("/main-functions", body);

export const updateMainFunction = (id: string, body: UpdateMainFunctionBody) => api.patch<MainFunctionListItem>(`/main-functions/${id}`, body);

export const deleteMainFunction = (id: string) => api.delete<null>(`/main-functions/${id}`);

export const createSubFunction = (body: CreateSubFunctionBody) => api.post<SubFunctionListItem>("/sub-functions", body);

export const updateSubFunction = (id: string, body: UpdateSubFunctionBody) => api.patch<SubFunctionListItem>(`/sub-functions/${id}`, body);

export const deleteSubFunction = (id: string) => api.delete<null>(`/sub-functions/${id}`);

/** `GET /main-functions/stats` — total, sub-functions total, a bucket per organisation type; one aggregate statement. */
export const mainFunctionStatsOptions = () =>
	queryOptions({
		queryKey: functionsKeys.mainStats,
		queryFn: ({ signal }) => api.get<MainFunctionStats>("/main-functions/stats", { signal }),
		staleTime: 60_000,
	});
